/**
 * @module PhoneNumberNormalizer
 * @description Utility functions for parsing, validating, and normalizing phone numbers across different countries.
 */

import { parsePhoneNumberWithError, type PhoneNumber, type CountryCode as CountryCodeType } from 'libphonenumber-js';

export type CountryCode = CountryCodeType;

interface NormalizeCountryOptions {
  addLeadingZero: boolean;
  trunkPrefix: string;
}

/**
 * @interface ParsedPhoneNumber
 * @description Represents the result of phone number parsing
 * @property {boolean} isValid - Whether the phone number is valid
 * @property {PhoneNumber} parsedNumber - The parsed phone number object
 */
export interface ParsedPhoneNumber {
  isValid: boolean;
  parsedNumber?: PhoneNumber;
}

/**
 * @interface CountryConfig
 * @description Configuration for phone number formatting in a specific country
 * @property {boolean} addLeadingZero - Whether to add leading zero to national numbers
 * @property {CountryCode} country - ISO country code
 * @property {string} countryCode - International dialing code (e.g., "+20" for Egypt)
 * @property {string} trunkPrefix - Trunk prefix (e.g., "57")
 */
export interface CountryConfig {
  addLeadingZero: boolean;
  country: CountryCode;
  countryCode: string;
  trunkPrefix: string;
}

/**
 * @interface Normalize
 * @description The normalized phone number result
 * @property {CountryCode} country - Country code
 * @property {boolean} isValid - Whether the number is valid
 * @property {string|null} [e164] - E.164 formatted number (if valid)
 * @property {string} asterisk - Number formatted for Asterisk PBX system
 */
export interface Normalize {
  country?: CountryCode;
  isValid: boolean;
  e164?: string;
  asterisk: string;
}

/**
 * Safely parses a phone number for a given country
 * @function safelyParsePhoneNumber
 * @param {string} number - The phone number to parse
 * @param {CountryCode} countryCode - ISO country code to validate against
 * @returns {ParsedPhoneNumber} Parsing result with validation status
 */
const safelyParsePhoneNumber = (number: string, countryCode: CountryCode): ParsedPhoneNumber => {
  try {
    const parsedNumber: PhoneNumber = parsePhoneNumberWithError(number, countryCode);
    const isValid: boolean =
      parsedNumber.isValid() && parsedNumber.isPossible() && parsedNumber.country === countryCode;
    return { isValid, parsedNumber };
  } catch {
    return { isValid: false, parsedNumber: undefined };
  }
};

/**
 * Formats a phone number for Asterisk PBX system
 * @function formatForAsterisk
 * @param {ParsedPhoneNumber} number - Parsed phone number object
 * @param {boolean} [addLeadingZero=false] - Whether to add leading zero
 * @param {string} [trunkPrefix=""] - National trunk prefix to prepend
 * @returns {string} Formatted number for Asterisk
 */
const formatForAsterisk = (
  number: ParsedPhoneNumber,
  addLeadingZero: boolean = false,
  trunkPrefix: string = '',
): string => {
  let nationalNumber: string = `${number.parsedNumber?.nationalNumber}`;

  if (addLeadingZero && !nationalNumber.startsWith('0')) {
    nationalNumber = `0${nationalNumber}`;
  }

  return `${trunkPrefix}${nationalNumber}`;
};

/**
 * Normalizes a phone number against multiple country configurations
 * @function normalize
 * @param {number|string} number - The phone number to normalize
 * @param {Record<CountryCode, CountryConfig>} countries - Country configurations to test against
 * @param {CountryCode} defaultCountry - Default country code if number isn't valid in any country
 * @returns {Normalize} Normalization result with validity and formatted numbers
 * @example
 * normalize("0123456789", countriesConfig, "EG");
 */
export const normalize = (
  number: number | string,
  countries: Record<CountryCode, CountryConfig>,
  defaultCountry: CountryCode,
): Normalize => {
  const numberToTest: string = `${number}`;

  const results: Normalize[] = [];

  for (const { country, addLeadingZero, trunkPrefix } of Object.values(countries)) {
    const result = normalizeCountry(numberToTest, country, { addLeadingZero, trunkPrefix });
    results.push(result);
  }

  const validResults = results.filter((r) => r.isValid);
  const defaultResult = results.find((v) => v.country === defaultCountry);

  if (validResults.length && !defaultResult?.isValid) {
    return validResults[0];
  } else if (defaultResult) {
    return defaultResult;
  } else {
    return {
      asterisk: number.toString(),
      isValid: false,
    };
  }
};

/**
 * Normalizes a phone number for a specific country
 * @function normalizeCountry
 * @param {string} number - The phone number to normalize
 * @param {CountryCode} country - Country code to validate against
 * @param {Partial<NormalizeCountryOptions>} [options={}] - Optional settings to customize the normalization process.
 * @param {boolean} [options.addLeadingZero=false] - If true, adds a leading zero to the formatted number, which is common in some regions.
 * @param {string} [options.trunkPrefix=''] - A national trunk prefix (like '0') to prepend to the number before formatting.
 * @returns {Normalize} Normalization result for the specific country
 */
const normalizeCountry = (
  number: string,
  country: CountryCode,
  { addLeadingZero = false, trunkPrefix = '' }: Partial<NormalizeCountryOptions> = {},
): Normalize => {
  const phoneNumber: ParsedPhoneNumber = safelyParsePhoneNumber(`${number}`, country);

  const asterisk: string = formatForAsterisk(phoneNumber, addLeadingZero, trunkPrefix);

  const isValid: boolean = phoneNumber.isValid;

  return {
    country,
    isValid,
    e164: isValid ? phoneNumber.parsedNumber?.format('E.164') : undefined,
    asterisk: isValid ? asterisk : number,
  };
};
